import { BaseController } from '../../base/controller';
import _ from 'lodash';

export class ConfirmFinancesController extends BaseController {
  constructor(
    controllerService,
    confirmFinancesService,
    TicketRequestType,
    creditorsService,
    ticketService,
    $scope,
    $q,
    FinanceTypes,
    TicketStates,
    hotkeys,
    uiModalDeclarationService,
  ) {
    'ngInject';
    super({
      controllerService,
      confirmFinancesService,
      TicketRequestType,
      creditorsService,
      ticketService,
      $scope,
      $q,
      FinanceTypes,
      TicketStates,
      hotkeys,
      uiModalDeclarationService,
    });
  }

  $onInit() {
    const { FinanceTypes, confirmFinancesService } = this;

    if (!this.split) {
      this.split = 'horizontal';
    }

    // Set translate key with maybe given translate prefix
    if (this.translatePrefix) {
      this.translateKey = `${this.translatePrefix}.finances`;
    } else {
      this.translateKey = 'finances';
    }

    // Start with the first tab as activeTab
    this.activeTab = FinanceTypes.directDebits.tabIndex;
    this.tabs = [
      {
        id: 'directDebits',
        templateUrl: confirmFinancesService.financesDirectDebitsTemplate,
        getHeader: () => 'directDebit',
      },
      {
        id: 'receivedPayments',
        templateUrl: confirmFinancesService.financesReceivedPaymentTemplate,
        getHeader: () => 'receivedPayments',
      },
      {
        id: 'standingOrders',
        templateUrl: confirmFinancesService.financesStandingOrdersTemplate,
        getHeader: () => 'standingOrders',
      },
    ];

    // Each tab id defines one finance type
    this.financeTypes = _.map(this.tabs, (tab) => tab.id);

    // Map available ticket documents to their finance type
    this.documents = this.confirmFinancesService.mapTicketDocuments(
      this.ticket.documents,
    );
    if (!this.documents.length) {
      // Use fallback document (which depends on the current status of the ticket)
      this.fallBackDocument =
        this.confirmFinancesService.getTicketFallbackDocument(this.ticket);
    }

    this.sequenceTypeOptions = this.confirmFinancesService.sequenceTypeOptions;
    this.intervalTypeOptions = this.confirmFinancesService.intervalTypeOptions;
    this.directDebitProperties =
      this.confirmFinancesService.directDebitProperties;
    this.receivedPaymentProperties =
      this.confirmFinancesService.receivedPaymentProperties;
    this.standingOrdersProperties =
      this.confirmFinancesService.standingOrdersProperties;

    this.paymentPartnerSuggest = {
      templateUrl: confirmFinancesService.PaymentPartnersSuggestTemplate,
      editable: true,
      delay: 200,
    };

    this.requiredMessage = {
      required: 'app.confirmFinaces.required',
    };

    this.bindHotkeys();
  }

  $onChanges(changes) {
    if (changes.finances) {
      this.loadFinances();
    }
  }

  get showFinancesSection() {
    return (
      this.featureCaptureBalanceListsByBank ||
      !(this.hasNoFinances && this.isNewBankCase)
    );
  }

  get hasNoFinances() {
    if (!this.finances) return false;
    return this.emptyFinances(this.finances);
  }

  get isNewBankCase() {
    return this.ticket.requestType === this.TicketRequestType.Incoming;
  }

  get showDeleteBalanceButton() {
    return this.isFeatureActive(
      this.FeatureNames.FF_USER_CAN_DELETE_BALANCELIST_ON_FINANCE_STATE,
    );
  }

  get showSubmitButton() {
    return (
      this.ticket &&
      (this.ticket.requestType === this.TicketRequestType.Outgoing ||
        this.featureCaptureBalanceListsByBank)
    );
  }

  get featureCaptureBalanceListsByBank() {
    return this.featureFlags.captureBalanceListsByBank;
  }

  loadFinances() {
    // Trigger initial change when all necessary finance data are available
    if (this.financeTypes) {
      this.financeTypes.map((type) => {
        // Create empty set of finances
        if (!this.finances[type]) {
          this.finances[type] = [];
        }
        let finances = this.finances[type];

        // Pre- or deselect all finances depending on their selection mode:
        // a) opt-in: deselect items by default
        // b) opt-out: preselect items by default
        _.forEach(finances, (item) => {
          item.selected = !this.optIn;
          this.updatePaymentPartner(type, item);
        });
      });
    }
  }

  financesChanged(finances, key = this.financeTypes[this.activeTab]) {
    this.finances[key] = finances;
  }

  updatePaymentPartner(type, paymentPartner) {
    _.forEach(this.finances[type], (item) => {
      if (item.paymentPartnerId === paymentPartner.paymentPartnerId) {
        item = paymentPartner;
      }
    });
  }

  getFinanceStatsByKey(key) {
    const selected = _.size(this.getFinancesSelectedByKey(key));
    const total = _.size(this.getFinancesByKey(key));
    return { selected, total };
  }

  getFinancesSelectedByKey(key) {
    return (this.getFinancesByKey(key) || []).filter((f) => f.selected);
  }

  getFinancesByKey(key) {
    return _.get(this.finances, key, []);
  }

  suggestDirectDebit(viewValue) {
    return this.creditorsService.find(viewValue);
  }

  suggestDirectDebitSelect($item) {
    const { creditorId, name } = $item;

    if (creditorId) {
      this.editor.directDebits.item.creditorId = creditorId;
    }
    if (name) {
      this.editor.directDebits.item.name = name;
    }
  }

  saveEditor(form, copy = false) {
    // Set form submitted state manually
    this.setFormSubmitted(form);

    if (form.$valid) {
      const { id } = this.tabs[this.activeTab];
      const item = this.getEditorItem(id);
      this.editor[id].save(item);
      this.resetEditor(form, copy && angular.copy(item));
    } else {
      this.setFormSubmitted(form, false);
    }
  }

  setFormSubmitted(form, submitted = true) {
    if (submitted) {
      this.isSubmitBusy = true;
      form.$setSubmitted();
    } else {
      this.$timeout(() => (this.isSubmitBusy = false));
    }
  }

  hideForm(hidden = true) {
    return this.$timeout(() => (this.formHidden = hidden));
  }

  resetEditor(form, defaultValues = null) {
    const { $timeout, $scope } = this;
    const { id } = this.tabs[this.activeTab];
    const item = this.getEditorItem(id);

    // NOTE: Hiding the form will avoid flickering of form validation
    this.hideForm().then(() => {
      $timeout(() => {
        // Reset the actual editor
        this.editor[id].reset(item);
      });

      // Delay the form reset and re-setup the form fields
      $timeout(() => {
        this.confirmFinancesService.resetForm(form);
        this.hideForm(false);
        this.setFormSubmitted(form, false);
        $scope.$broadcast(`${id}.setupFields`, defaultValues);
      });
    });
  }

  getEditorItem(id) {
    const { item } = this.editor[id];
    return item;
  }

  prepareFinances(finances, defaultValue = []) {
    const preparedFinances = angular.copy(finances);
    return _.forEach(preparedFinances, (value, key) => {
      if (!value) {
        preparedFinances[key] = defaultValue;
      } else {
        // Filter only selected finance items
        preparedFinances[key] = value.filter((i) => i.selected);
      }
    });
  }

  emptyFinances(finances) {
    return _.some(finances, _.isEmpty);
  }

  openConfirmModal(key) {
    const defer = this.$q.defer();
    const { $scope } = this;

    $scope.confirmTitle = `${key}.title`;
    $scope.confirmBody = `${key}.body`;
    $scope.confirmDismiss = `${key}.dismiss`;
    $scope.confirmOk = `${key}.confirm`;

    this.uiModalDeclarationService.openUrl({
      url: 'app/common/ui/needsConfirmation/confirmation.tpl.html',
      options: {
        scope: $scope,
        onSuccess: () => defer.resolve(),
        onDismiss: () => defer.reject(),
        modalClass: 'confirmation-modal',
      },
    });

    return defer.promise;
  }

  back($event) {
    if (this.onBack) {
      this.onBack();
    }
  }

  openDeleteBalanceListModal() {
    this.openConfirmModal('app.confirmFinaces.deleteBalanceList').then(() => {
      this.ticketService.deleteUploadedBalanceList(this.ticket.id);
    });
  }

  submit($event) {
    // Prepare available finances for submit
    const finances = this.prepareFinances(this.finances);

    // Indicate empty finances with a warning
    if (this.emptyFinances(finances)) {
      return this.openConfirmModal(
        'app.ticket.finances.form.confirmation',
      ).then(() => this.onSubmit({ finances }));
    }
    this.onSubmit({ finances });
  }

  demo() {
    this.fillRandomData();
  }

  fillRandomData() {
    if (this.envService.isProduction()) {
      return;
    }

    switch (this.activeTab) {
      case 0: {
        this.confirmFinancesService.fillFormData(
          this.changeDirectDebitForm,
          this.editor['directDebits'],
        );
        break;
      }
      case 1: {
        this.confirmFinancesService.fillFormData(
          this.changeReceivedPaymentsForm,
          this.editor['receivedPayments'],
        );
        break;
      }
      case 2: {
        this.confirmFinancesService.fillFormData(
          this.changeStandingOrdersForm,
          this.editor['standingOrders'],
        );
        break;
      }
    }
  }

  bindHotkeys() {
    const { hotkeys, $scope } = this;

    if (!this.envService.isProduction()) {
      // Bind Hotkeys to the controllers scope (will automatically unbind
      // the hotkey when the scope is destroyed)
      hotkeys.bindTo($scope).add({
        combo: 'ctrl+l',
        description: 'Toggle dev mode',
        callback: () => this.toggleDevMode(),
      });
    }

    // Bind bulk mode hotkey
    hotkeys.bindTo($scope).add({
      combo: 'ctrl+i',
      description: 'Toggle Bulk-Import',
      callback: () => this.toggleBulkImport(),
    });
  }

  bulkImport(items) {
    const { id } = this.tabs[this.activeTab];
    this.editor[id].addAll(items);
  }

  toggleBulkImport() {
    this.enableBulkImport = !this.enableBulkImport;
  }
}
